import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../main/Apicall";
import Pagination from "../Reusablecomponents/Pagination";
import ExportButton from "../Reusablecomponents/ExportButton";
import { FaEdit, FaTrash, FaPlus, FaEye, FaFileExport, FaUpload } from "react-icons/fa";
import FunctionEditChecklistModal from "../Popups/FunctionEditChecklistModal";
import FunctionDeleteConfirmationModal from "../Popups/FunctionDeleteConfirmationModal";
import FunctionCreateChecklistModal from "../Popups/FunctionCreateChecklistModal";
import ToastPopup from "../Popups/ToastPopup";
import "../css/others/Table.css";
import * as XLSX from 'xlsx-js-style';
import axios from "axios";
import { createToastMessage, ToastMessage } from "../Interfaces/Interface";
import EncryptionUtils from "../main/encryptionUtils";
const FunctionChecklist: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [, setUploadError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editableDetails, setEditableDetails] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [newFunctionChecklistName, setNewFunctionChecklistName] = useState<string>("");
  const [newFunctionChecklistDescription, setNewFunctionChecklistDescription] = useState<string>("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [deleteFunctionTemplateId, setDeleteFunctionTemplateId] = useState<string | null>(null);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [activeTab, setActiveTab] = useState<"DRAFT" | "ACTIVE">("DRAFT");
  const [selectedChecklists, setSelectedChecklists] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [isPublishConfirmModalOpen, setIsPublishConfirmModalOpen] = useState<boolean>(false);
  const [publishingChecklistId, setPublishingChecklistId] = useState<string | null>(null);
  const [isLoadingQuestions] = useState<boolean>(false);
  const navigate = useNavigate();
  const modifiedBy = EncryptionUtils.getLocalStorage<string>("email") || "";
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = "";
      if (activeTab === "DRAFT") {
        endpoint = "/api/function/templates/by-status";
      } else {
        endpoint = "/api/function/templates";
      }
      const params: any = { page: currentPage - 1, size: itemsPerPage };
      if (activeTab === "DRAFT") {
        params.status = "DRAFT";
      }
      const response = await apiCall("get", endpoint, undefined, params);
      const { content, totalElements, totalPages } = response;
      setData(content);
      setTotalElements(totalElements);
      setTotalPages(totalPages);
      setSelectedChecklists([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, activeTab]);
  useEffect(() => { fetchData(); }, [fetchData]);
  const fetchAllDataForExport = useCallback(async () => {
    try {
      let endpoint = "";
      if (activeTab === "DRAFT") {
        endpoint = "/api/function/templates/by-status";
      } else {
        endpoint = "/api/function/templates";
      }
      const params: any = { page: 0, size: totalElements };
      if (activeTab === "DRAFT") {
        params.status = "DRAFT";
      }
      const response = await apiCall("get", endpoint, undefined, params);
      return response.content?.map((row: any) => ({
        "Function CheckList Name": row.functionTemplateName,
        "Function Description": row.functionDescription,
        "Status": row.status,
      })) || [];
    } catch (error) {
      console.error("Error exporting data:", error);
      return [];
    }
  }, [totalElements, activeTab]);
  const handleSelectChecklist = (id: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedChecklists((prev) => [...prev, id]);
    } else {
      setSelectedChecklists((prev) => prev.filter((item) => item !== id));
      setSelectAll(false);
    }
  };
  const handleSelectAll = (isChecked: boolean) => {
    setSelectAll(isChecked);
    if (isChecked) {
      const allIds = data.map((item) => item.functionTemplateId);
      setSelectedChecklists(allIds);
    } else {
      setSelectedChecklists([]);
    }
  };
  const handlePublishSelected = async () => {
    if (selectedChecklists.length === 0) {
      setMessage(createToastMessage("No checklists selected.", "warning", 3000));
      return;
    }
    const checklistsWithoutQuestions: string[] = [];
    for (const templateId of selectedChecklists) {
      try {
        const response = await apiCall("get", `/api/function/templates/${templateId}/questions/edit`, null, { page: 0, size: 1 });
        const hasQuestions = response.content && response.content.length > 0;
        if (!hasQuestions) {
          const checklist = data.find(d => d.functionTemplateId === templateId);
          checklistsWithoutQuestions.push(checklist?.functionTemplateName || templateId);
        }
      } catch {
        const checklist = data.find(d => d.functionTemplateId === templateId);
        checklistsWithoutQuestions.push(checklist?.functionTemplateName || templateId);
      }
    }
    if (checklistsWithoutQuestions.length > 0) {
      setMessage(createToastMessage(`Please add questions for: ${checklistsWithoutQuestions.join(", ")}`, "warning", 3000));
      return;
    }
    try {
      for (const templateId of selectedChecklists) {
        await apiCall("post", `/api/function/templates/${templateId}/publish`);
      }
      setMessage(createToastMessage("Function checklists published successfully!", "success", 3000));
      fetchData();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to publish selected checklists";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const confirmPublish = async () => {
    if (publishingChecklistId) {
      try {
        const response = await apiCall("get", `/api/function/templates/${publishingChecklistId}/questions/edit`, null, { page: 0, size: 1 });
        const hasQuestions = response.content && response.content.length > 0;
        if (!hasQuestions) {
          setMessage(createToastMessage("Please add questions before publishing.", "warning", 3000));
          setIsPublishConfirmModalOpen(false);
          setPublishingChecklistId(null);
          return;
        }
        await apiCall("post", `/api/function/templates/${publishingChecklistId}/publish`);
        setMessage(createToastMessage("Function checklist published successfully!", "success", 3000));
        fetchData();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Failed to publish checklist";
        setMessage(createToastMessage(errorMessage, "error", 3000));
      } finally {
        setIsPublishConfirmModalOpen(false);
        setPublishingChecklistId(null);
      }
    }
  };
  const handleDetailsPage = (functionTemplateId: string, status: string) => {
    navigate(`/functionchecklistdetails/${functionTemplateId}`, { state: { status } });
  };
  const handleViewDetails = (functionTemplateId: string) => {
    navigate(`/functionviewchecklistdetails/${functionTemplateId}`);
  };
  const handleEditClick = (row: any) => {
    setEditableDetails(row);
    setIsEditModalOpen(true);
  };
  const handleDelete = (functionTemplateId: string) => {
    setDeleteFunctionTemplateId(functionTemplateId);
    setIsDeleteModalOpen(true);
  };
  const confirmDelete = async () => {
    if (deleteFunctionTemplateId) {
      try {
        await apiCall("delete", `/api/function/templates/${deleteFunctionTemplateId}`);
        setMessage(createToastMessage("Function Checklist deleted successfully!", "success", 3000));
        fetchData();
      } catch (error) {
        setMessage(createToastMessage(`Failed to delete Function checklist: ${error instanceof Error ? error.message : String(error)}`, "error", 3000));
      } finally {
        setIsDeleteModalOpen(false);
        setDeleteFunctionTemplateId(null);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setUploadError("");
    }
  };
  const handleBulkUpload = async () => {
    if (!file) {
      setUploadError("Please choose a file before uploading.");
      return;
    }
    try {
      const presignedUrlResponse = await apiCall("get", `/api/bulk-upload/s3presigneduploadurl?fileName=${file.name}&action=mgmt_function_checklist_creation`);
      const presignedUrl = presignedUrlResponse;
      if (!presignedUrl) {
        throw new Error("Failed to retrieve presigned upload URL.");
      }
      await axios.put(presignedUrl, file, { headers: { "Content-Type": file.type } });
      setMessage(createToastMessage("File uploaded successfully!", "success", 3000));
      const urlObj = new URL(presignedUrl);
      const fileKey = decodeURIComponent(urlObj.pathname.substring(1));
      await apiCall("post", "/api/bulk-upload/complete", { fileKey });
      await apiCall("get", "/api/bulk-upload/process-messages");
      setMessage(createToastMessage("File processed successfully!", "success", 2500));
      setTimeout(() => { window.location.reload() }, 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage || "Error during bulk upload", "error", 3000));
    }
  };
  const handleSaveChanges = async () => {
    if (editableDetails) {
      try {
        await apiCall("put", `/api/function/templates/${editableDetails.functionTemplateId}`, { functionTemplateName: editableDetails.functionTemplateName, functionDescription: editableDetails.functionDescription, updatedBy: modifiedBy });
        setMessage(createToastMessage("Function Checklist updated successfully!", "success", 3000));
        setIsEditModalOpen(false);
        fetchData();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Failed to update checklist";
        setMessage(createToastMessage(errorMessage, "error", 3000));
      }
    }
  };
  const handleOpenCreateModal = () => { setIsCreateModalOpen(true); };
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewFunctionChecklistName("");
    setNewFunctionChecklistDescription("");
  };
  const handleSaveNewChecklist = async () => {
    const newFunctionChecklist = { functionTemplateName: newFunctionChecklistName, functionDescription: newFunctionChecklistDescription, createdBy: modifiedBy };
    try {
      await apiCall("post", "/api/function/templates", newFunctionChecklist);
      setMessage(createToastMessage("Function Checklist created successfully!", "success", 3000));
      setIsCreateModalOpen(false);
      fetchData();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to create checklist";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const handleExportChecklist = async (functionTemplateId: any, functionTemplateName: any) => {
    try {
      const endpoint = activeTab === "DRAFT" ? `/api/function/templates/${functionTemplateId}/questions/edit` : `/api/function/templates/${functionTemplateId}/questions`;
      const response = await apiCall("get", endpoint, undefined, { page: 0, size: 2_147_483_647 });
      const questions = response.content || [];
      if (questions.length === 0) {
        setMessage(createToastMessage("No questions available to export.", "warning", 3000));
        return;
      }
      const fieldHeaderMap: { [key: string]: string } = { functionAreaName: "Audit Area", questionText: "Audit Question", evidenceRequired: "Required Evidence", isoClauseReference: "ISO Clause", annexAControlReference: "Annex A Control Mapping" };
      const selectedFields = Object.keys(fieldHeaderMap);
      const dataForExport = questions.map((q: { [key: string]: any }) => {
        const row: { [key: string]: any } = {};
        selectedFields.forEach((field) => {
          row[fieldHeaderMap[field]] = q[field] ?? "";
        });
        return row;
      });
      const worksheet = XLSX.utils.json_to_sheet(dataForExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Function Questions");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${functionTemplateName}_Questions.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(createToastMessage(`Failed to export Function checklist questions: ${error}`, "error", 3000));
    }
  };
  const handleDownloadTemplate = async () => {
    try {
      const response = await apiCall("get", "/api/bulk-upload/s3presigneddownloadurl", null, { fileName: "function_checklist_template.xlsx", action: "default" });
      const presignedUrl = response;
      if (!presignedUrl) throw new Error("Failed to retrieve presigned URL");
      const fileResponse = await axios.get(presignedUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "function_checklist_template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setMessage(createToastMessage("Template downloaded successfully.", "success", 3000));
    } catch (error) {
      setMessage(createToastMessage("Failed to download the template.", "error", 3000));
    }
  };
  const triggerFileInput = () => { fileInputRef.current?.click() };
  const Columns = [{ key: "Function CheckList Name", label: "Function CheckList Name" }, { key: "Function Description", label: "Function Description" }, { key: "Status", label: "Status" }];
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Function Checklists</h2>
        <div className="table-title-actions">
          <button className="create-function-checklist" onClick={handleOpenCreateModal}>Create Function Checklist</button>
          <button className="download-template" onClick={handleDownloadTemplate}>Download Function Checklist template</button>
          <button type="button" className={`upload-checklist${file ? " disabled" : ""}`} onClick={triggerFileInput} disabled={!!file}>Upload Function Checklist</button>
          <input ref={fileInputRef} type="file" accept=".csv, .xlsx, .json" onChange={handleFileChange} className="hidden-input" disabled={!!file} />
          {file && (<button onClick={handleBulkUpload} className="upload-file"><FaUpload />Upload File</button>)}
          {activeTab === "DRAFT" && (
            <button className="publish" onClick={handlePublishSelected}>Publish</button>
          )}
          <ExportButton columns={Columns} fileName={`Function_CheckLists_${activeTab}`} fetchAllData={fetchAllDataForExport} data={[]} />
        </div>
      </div>
      <div className="tabs-container">
        <button className={`tab-button ${activeTab === "DRAFT" ? "active" : ""}`} onClick={() => setActiveTab("DRAFT")}>Draft Checklists</button>
        <button className={`tab-button ${activeTab === "ACTIVE" ? "active" : ""}`} onClick={() => setActiveTab("ACTIVE")}>Active Checklists</button>
      </div>
      <>
        <div className="table-container">
          <div className="table-inner-container">
            <table className="main-table">
              <thead>
                <tr>
                  {activeTab === "DRAFT" && <th><input type="checkbox" checked={selectAll} onChange={(e) => handleSelectAll(e.target.checked)} /></th>}
                  <th>S.No.</th>
                  <th>Function CheckList Name</th>
                  <th>Function Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(data) && data.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === "DRAFT" ? 6 : 5} className="no-data">No data available</td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <tr key={row.functionTemplateId}>
                      {activeTab === "DRAFT" && (
                        <td>
                          <input type="checkbox" checked={selectedChecklists.includes(row.functionTemplateId)} onChange={(e) => handleSelectChecklist(row.functionTemplateId, e.target.checked)} />
                        </td>
                      )}
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>{row.functionTemplateName || "N/A"}</td>
                      <td>{row.functionDescription || "N/A"}</td>
                      <td>
                        {activeTab === "ACTIVE" && (
                          <FaEye className="action-icon" onClick={() => handleViewDetails(row.functionTemplateId)} title="View Questions" />
                        )}
                        <FaPlus className="action-icon" onClick={() => handleDetailsPage(row.functionTemplateId, row.status)} title="Add Questions" style={{ cursor: isLoadingQuestions ? 'wait' : 'pointer' }} />
                        {activeTab === "DRAFT" && (
                          <>
                            <FaEdit className="action-icon" onClick={() => handleEditClick(row)} title="Edit Checklist" />
                            <FaTrash className="action-icon" onClick={() => handleDelete(row.functionTemplateId)} title="Delete Checklist" />
                          </>
                        )}
                        <FaFileExport className="action-icon" onClick={() => handleExportChecklist(row.functionTemplateId, row.functionTemplateName)} title="Download File" />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalElements={totalElements}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </>
      <FunctionEditChecklistModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        editableDetails={editableDetails}
        setEditableDetails={setEditableDetails}
        onSave={handleSaveChanges}
      />
      <FunctionCreateChecklistModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        newFunctionChecklistName={newFunctionChecklistName}
        setNewFunctionChecklistName={setNewFunctionChecklistName}
        newFunctionChecklistDescription={newFunctionChecklistDescription}
        setNewFunctionChecklistDescription={setNewFunctionChecklistDescription}
        onSave={handleSaveNewChecklist}
      />
      <FunctionDeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />
      {isPublishConfirmModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Are you sure you want to publish this checklist?</h3>
            <p>This will make the checklist active and available for use.</p>
            <div className="buttons">
              <button className="cancelbutton" onClick={() => setIsPublishConfirmModalOpen(false)}>Cancel</button>
              <button className="confirmbutton" onClick={confirmPublish}>Yes, Publish</button>
            </div>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default FunctionChecklist;